import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApprovalThreadEntry, PrintColorOption } from '@customedge/shared';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ApprovalPreview } from './ApprovalPreview';
import { useConfirm } from './Notifications';
import { InfoTooltip } from './tools/shared/InfoTooltip';
import { useApprovalEvents } from '../hooks/useApprovalEvents';
import { playApprovalNotificationSound, showApprovalNotification, requestApprovalNotificationPermission } from '../lib/approvalNotify';

// Public, unauthenticated design-approval page — the customer opens this via
// a link an admin generated in the CustomEdge admin's Orders detail drawer
// (see admin/src/components/OrderDetailDrawer.tsx). No login: the URL's
// :token itself (an unguessable 24-byte value, see backend/src/routes/
// approval.ts) is the only credential, matching the `?design=` share link's
// own "possession of the URL is the permission" model.
interface ApprovalData {
  // Needed so the 2D/3D preview can set the shared designStore's
  // savedDesignId — see ApprovalPreview/DesignPreviewCanvas's own comment for
  // why, without it, the embroidery/embossing/laser-engraving/doming preview
  // modules never rendered on this page (found 2026-09-01).
  designId: string;
  productId: string;
  productSku: string | null;
  title: string | null;
  approvalStatus: 'pending' | 'approved' | 'changes_requested';
  approvalNote: string | null;
  previewUrl: string | null;
  jsonObjects: Record<string, any>;
  // The Object Variants the customer picked at design time — applied to the
  // 2D/3D preview so they review the exact variant they chose, not defaults.
  selectedVariantOptions?: Record<string, string>;
  // The customer's saved vendor Product Color Variant + print method — a
  // different selection from selectedVariantOptions above (see
  // DesignPreviewCanvas's own comment for the full explanation).
  colorVariantId?: string | null;
  printMethod?: string | null;
  // The multiselect print-position feature's own per-position resolution —
  // see DesignPreviewCanvas's own comment for why these are needed
  // separately from the flat colorVariantId/printMethod above.
  printPositions?: string[];
  printMethodByPosition?: Record<string, string>;
  printColorByPosition?: Record<string, PrintColorOption>;
  // Every approve/request-changes + follow-up reply on this design, customer
  // and admin interleaved oldest-first — see shared/src/types/order.ts.
  thread: ApprovalThreadEntry[];
}

// An image the customer picked to attach to a "request changes" note.
// Uploads immediately on selection (POST /:token/upload) rather than waiting
// for final submit — `id` is null while that upload is in flight, so the
// thumbnail can show a spinner and `respond()` knows to wait rather than
// submit a partial imageIds list.
interface AttachedImage {
  key: string;
  previewUrl: string;
  id: string | null;
  uploading: boolean;
  error: string | null;
}

export function ApprovalPage({ token }: { token: string }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [data, setData] = useState<ApprovalData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [showNoteBox, setShowNoteBox] = useState(false);
  const [submitting, setSubmitting] = useState<'approved' | 'changes_requested' | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [images, setImages] = useState<AttachedImage[]>([]);

  // Separate state for the "keep talking after a decision" reply box — kept
  // apart from the pre-decision note/images above so submitting one never
  // clobbers text/images the customer has queued in the other.
  const [replyNote, setReplyNote] = useState('');
  const [replyImages, setReplyImages] = useState<AttachedImage[]>([]);
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  const load = () => {
    fetch(`/api/approval/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setData)
      .catch(() => setError(t('approval.notFound')));
  };

  useEffect(load, [token]);

  // Real-time push (fixed 2026-09-23, user report: "chat is not updating
  // automatically both admin and user view") — GET /api/events/design/:id
  // (see backend/src/services/approvalEvents.ts) fans out every new
  // ApprovalComment to both open views immediately, instead of the customer
  // only ever seeing the admin's reply on their next manual reload. Appends
  // directly into `data.thread` rather than calling `load()` again: a full
  // refetch here would also be correct, but would re-flash the whole
  // preview panel (ApprovalPreview re-renders off `data` too) for what's
  // really just one new thread entry. Dedupes by id since this customer's
  // OWN message also arrives back over this same SSE connection (the server
  // has no way to distinguish "the tab that sent it" from any other open
  // connection) — `respond`/`sendReply` below already append+re-render via
  // their own `load()` call on success, so without this guard every one of
  // the customer's own messages would appear twice for a moment.
  const receivedIdsRef = useRef<Set<string>>(new Set());
  useApprovalEvents(data?.designId, (comment) => {
    if (receivedIdsRef.current.has(comment.id)) return;
    receivedIdsRef.current.add(comment.id);
    setData(prev => {
      if (!prev) return prev;
      if (prev.thread.some(e => e.id === comment.id)) return prev;
      return { ...prev, thread: [...prev.thread, comment] };
    });
    // The receiver here is the customer — only cue for a message that
    // actually came from the OTHER side (admin), never for the echo of the
    // customer's own just-sent message arriving back over this same stream.
    if (comment.author === 'admin') {
      playApprovalNotificationSound();
      showApprovalNotification(t('approval.newMessageNotificationTitle'), comment.note || t('approval.newMessageNotificationBody'));
    }
  });

  // Keep the latest message in view without the user having to scroll for
  // it — mirrors any chat UI's "always land on the newest message" behavior.
  // Fires on the initial load (data.thread going from undefined to populated)
  // and again every time a new entry is appended (customer's own send, a
  // fresh admin reply pulled in by a later `load()`, or the SSE push above).
  const threadEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: 'end' });
  }, [data?.thread.length]);

  // Ask for Notification permission once, from a real user gesture (the
  // Notification API requires one — see approvalNotify.ts's own comment) —
  // the customer's first interaction with the note/reply box is as good a
  // moment as any, and asking on page load with no gesture at all would
  // just silently fail in most browsers anyway.
  const notificationPermissionRequestedRef = useRef(false);
  const ensureNotificationPermissionRequested = () => {
    if (notificationPermissionRequestedRef.current) return;
    notificationPermissionRequestedRef.current = true;
    requestApprovalNotificationPermission();
  };

  const uploadImage = async (file: File, setter: React.Dispatch<React.SetStateAction<AttachedImage[]>>) => {
    const key = `${file.name}-${file.size}-${Date.now()}`;
    setter(prev => [...prev, { key, previewUrl: URL.createObjectURL(file), id: null, uploading: true, error: null }]);

    try {
      const fd = new FormData();
      fd.set('image', file);
      const res = await fetch(`/api/approval/${token}/upload`, { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const { id } = await res.json();
      setter(prev => prev.map(img => img.key === key ? { ...img, id, uploading: false } : img));
    } catch (err: any) {
      setter(prev => prev.map(img => img.key === key ? { ...img, uploading: false, error: err.message ?? t('approval.uploadFailed') } : img));
    }
  };

  const respond = async (decision: 'approved' | 'changes_requested') => {
    if (decision === 'changes_requested' && !note.trim()) {
      setShowNoteBox(true);
      return;
    }
    // Uploads run in the background as each file is picked — if the customer
    // hits a decision button while one is still in flight, wait rather than
    // submit a comment that's silently missing an image they clearly meant
    // to attach.
    if (images.some(img => img.uploading)) {
      setSubmitError(t('approval.waitForUploads'));
      return;
    }
    // Approval is the one decision here that's hard to walk back (it signs
    // off the design as print-ready) — confirm before submitting it, same as
    // this app's own destructive-action pattern elsewhere (useConfirm, e.g.
    // TopToolbar's canvas reset). "Request changes" needs no such gate: it's
    // already the reversible, "not yet" choice, and the note box the customer
    // just filled in is itself a confirming step.
    if (decision === 'approved') {
      const ok = await confirm(t('approval.confirmApprove'), { confirmLabel: t('approval.approve') });
      if (!ok) return;
    }
    setSubmitting(decision);
    setSubmitError(null);
    try {
      const imageIds = images.filter(img => img.id && !img.error).map(img => img.id as string);
      const res = await fetch(`/api/approval/${token}/respond`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ decision, note: note.trim() || undefined, imageIds: imageIds.length ? imageIds : undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      load();
    } catch (err: any) {
      setSubmitError(err.message ?? t('approval.submitFailed'));
    } finally {
      setSubmitting(null);
    }
  };

  const sendReply = async () => {
    if (!replyNote.trim() && !replyImages.length) return;
    if (replyImages.some(img => img.uploading)) {
      setReplyError(t('approval.waitForUploads'));
      return;
    }
    setReplySubmitting(true);
    setReplyError(null);
    try {
      const imageIds = replyImages.filter(img => img.id && !img.error).map(img => img.id as string);
      const res = await fetch(`/api/approval/${token}/reply`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ note: replyNote.trim() || undefined, imageIds: imageIds.length ? imageIds : undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setReplyNote('');
      setReplyImages([]);
      load();
    } catch (err: any) {
      setReplyError(err.message ?? t('approval.submitFailed'));
    } finally {
      setReplySubmitting(false);
    }
  };

  if (error) {
    return (
      <div style={wrapStyle}>
        <p style={{ color: '#ef4444', fontSize: 15 }}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={wrapStyle}>
        <p style={{ color: '#64748b', fontSize: 14 }}>{t('approval.loading')}</p>
      </div>
    );
  }

  const decided = data.approvalStatus !== 'pending';

  return (
    <div style={wrapStyle}>
      <div style={cardStyle}>
        {/* Header stays outside the two-column split below and never
            scrolls — this is the fix for the reported bug: with many
            messages, the old single-column layout grew the whole card
            downward and pushed the title/language-switcher off screen. */}
        <div style={{ padding: '20px 24px 12px', flexShrink: 0, borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>
              {t('approval.title')}
            </h1>
            <LanguageSwitcher />
          </div>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
            {t('approval.subtitle', { product: data.title || data.productId })}
          </p>
        </div>

        {/* Body: preview on the left, messaging + actions on the right —
            each column scrolls independently and is capped to the card's
            own fixed height, so neither the preview nor a long thread can
            push the header out of view. Stacks vertically below ~720px
            (phones/narrow windows), where a fixed side-by-side split isn't
            usable, back to top-to-bottom with the preview first. */}
        <div className="approval-body" style={bodyStyle}>
          <div className="approval-left-col" style={leftColStyle}>
            {/* Prefer the interactive 2D/3D preview (real product photo + 3D
                wrap, with zoom) when we could resolve the product's SKU —
                falls back to the flat rendered image, then a "no preview" note. */}
            {data.productSku ? (
              <ApprovalPreview
                designId={data.designId}
                sku={data.productSku}
                jsonObjects={data.jsonObjects}
                selectedVariantOptions={data.selectedVariantOptions}
                colorVariantId={data.colorVariantId}
                printMethod={data.printMethod}
                printPositions={data.printPositions}
                printMethodByPosition={data.printMethodByPosition}
                printColorByPosition={data.printColorByPosition}
              />
            ) : data.previewUrl ? (
              <img src={data.previewUrl} alt="" style={{ width: '100%', borderRadius: 8, border: '1px solid #e2e8f0', display: 'block' }} />
            ) : (
              <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>{t('approval.noPreview')}</p>
            )}
          </div>

          <div style={rightColStyle}>
            {/* Scrollable thread — the ONLY part of the page that grows with
                message count. `threadEndRef` is scrolled into view whenever
                a message is added (see the effect above), so the customer
                always lands on the newest message instead of having to
                scroll down for it themselves. */}
            <div style={threadScrollStyle}>
              {decided && (
                <div style={{
                  padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 12,
                  background: data.approvalStatus === 'approved' ? '#16a34a20' : '#ef444420',
                  color:      data.approvalStatus === 'approved' ? '#16a34a' : '#ef4444',
                }}>
                  {data.approvalStatus === 'approved' ? t('approval.alreadyApproved') : t('approval.alreadyRequestedChanges')}
                </div>
              )}

              {data.thread.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {data.thread.map(entry => <ThreadEntryCard key={entry.id} entry={entry} t={t} />)}
                </div>
              ) : !decided && (
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>{t('approval.threadEmpty')}</p>
              )}
              <div ref={threadEndRef} />
            </div>

            {/* Actions — pinned to the bottom of the right column (never the
                bottom of the page), same spot for both the initial decision
                and every reply after it. */}
            <div style={composerStyle}>
              {!decided && (
                <>
                  {showNoteBox && (
                    <>
                      <textarea
                        autoFocus
                        value={note}
                        onChange={e => setNote(e.target.value)}
                        placeholder={t('approval.notePlaceholder')}
                        rows={2}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 8, resize: 'vertical' }}
                      />
                      <ImagePickerRow images={images} onFiles={files => Array.from(files).forEach(f => uploadImage(f, setImages))} onRemove={key => setImages(prev => prev.filter(img => img.key !== key))} />
                    </>
                  )}

                  {submitError && <p style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>{submitError}</p>}

                  <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                    <button
                      onClick={() => respond('approved')}
                      disabled={submitting !== null}
                      style={{ flex: 1, padding: '10px 16px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}
                    >
                      {submitting === 'approved' ? t('approval.submitting') : t('approval.approve')}
                    </button>
                    <button
                      onClick={() => respond('changes_requested')}
                      disabled={submitting !== null}
                      style={{ flex: 1, padding: '10px 16px', background: showNoteBox ? '#ef4444' : '#f1f5f9', color: showNoteBox ? 'white' : '#374151', border: 'none', borderRadius: 8, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}
                    >
                      {submitting === 'changes_requested' ? t('approval.submitting') : t('approval.requestChanges')}
                    </button>
                  </div>
                </>
              )}

              <div style={{ paddingTop: decided ? 0 : 12, borderTop: decided ? 'none' : '1px solid #e2e8f0' }}>
                <textarea
                  value={replyNote}
                  onChange={e => setReplyNote(e.target.value)}
                  onFocus={ensureNotificationPermissionRequested}
                  placeholder={t('approval.replyPlaceholder')}
                  rows={2}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginBottom: 8, resize: 'vertical' }}
                />
                <ImagePickerRow images={replyImages} onFiles={files => Array.from(files).forEach(f => uploadImage(f, setReplyImages))} onRemove={key => setReplyImages(prev => prev.filter(img => img.key !== key))} />
                {replyError && <p style={{ color: '#ef4444', fontSize: 12, margin: '0 0 8px' }}>{replyError}</p>}
                <button
                  onClick={sendReply}
                  disabled={replySubmitting || (!replyNote.trim() && !replyImages.length)}
                  className="btn btn-primary"
                  style={{
                    fontSize: 13,
                    cursor: replySubmitting ? 'not-allowed' : 'pointer',
                    opacity: (!replyNote.trim() && !replyImages.length) ? 0.5 : 1,
                  }}
                >
                  {replySubmitting ? t('approval.submitting') : t('approval.sendReply')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Below ~720px a fixed side-by-side split leaves both columns too
            narrow to use — stack them instead, preview first, matching how
            a phone-width chat/app screen normally orders "content, then
            conversation". Deliberately does NOT make `.approval-body` itself
            scroll (an earlier version did, and it fought with the thread's
            own scrollIntoView-to-bottom for the same scroll gesture, landing
            mid-transition between the preview and the latest message). The
            preview instead gets a capped height so the right column keeps
            its full own height — and therefore its own single scroll region
            for the thread, composer pinned right under it, same as desktop. */}
        <style>{`
          @media (max-width: 720px) {
            .approval-body { flex-direction: column; }
            .approval-left-col { border-right: none; border-bottom: 1px solid #e2e8f0; flex: 0 0 auto; height: 38vh; }
          }
        `}</style>
      </div>
    </div>
  );
}

function ThreadEntryCard({ entry, t }: { entry: ApprovalThreadEntry; t: (key: string, opts?: any) => string }) {
  const isAdmin = entry.author === 'admin';
  const decisionLabel = entry.decision === 'approved'
    ? t('approval.threadApproved')
    : entry.decision === 'changes_requested' ? t('approval.threadChangesRequested') : null;

  return (
    <div style={{
      alignSelf: isAdmin ? 'flex-start' : 'flex-end',
      maxWidth: '85%',
      background: isAdmin ? '#f1f5f9' : 'var(--selection-tint)',
      border: `1px solid ${isAdmin ? '#e2e8f0' : 'color-mix(in srgb, var(--selection) 40%, white)'}`,
      borderRadius: 10,
      padding: 10,
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: isAdmin ? '#334155' : 'var(--selection-text)' }}>
          {isAdmin ? t('approval.threadAdmin') : t('approval.threadYou')}
          {decisionLabel && <span style={{ fontWeight: 500, color: '#64748b' }}> · {decisionLabel}</span>}
        </span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>{new Date(entry.createdAt).toLocaleString()}</span>
      </div>
      {entry.note && <p style={{ margin: 0, fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap' }}>{entry.note}</p>}
      {entry.images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {entry.images.map(img => (
            <a key={img.id} href={img.url} target="_blank" rel="noopener noreferrer">
              <img src={img.url} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// Shared by the initial decision note box and the always-available reply box
// below it — both need identical "pick files → upload immediately → show
// thumbnail/spinner/remove" behavior, differing only in which state array
// they read/write (passed in by the caller).
function ImagePickerRow({ images, onFiles, onRemove }: {
  images: AttachedImage[];
  onFiles: (files: FileList) => void;
  onRemove: (key: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        {images.map(img => (
          <div key={img.key} style={{ position: 'relative', width: 56, height: 56 }}>
            <img
              src={img.previewUrl}
              alt=""
              style={{
                width: 56, height: 56, objectFit: 'cover', borderRadius: 6,
                border: img.error ? '1px solid #ef4444' : '1px solid #e2e8f0',
                opacity: img.uploading ? 0.5 : 1,
              }}
            />
            {img.uploading && (
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#64748b' }}>
                {t('approval.uploading')}
              </span>
            )}
            <button
              onClick={() => onRemove(img.key)}
              title={t('approval.removeImage')}
              style={{
                position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%',
                background: '#374151', color: 'white', border: '2px solid white', fontSize: 11,
                lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              }}
            >×</button>
          </div>
        ))}

        <label
          title={t('approval.attachImage')}
          style={{
            width: 56, height: 56, borderRadius: 6, border: '1px dashed #cbd5e1', color: '#94a3b8',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, cursor: 'pointer', flexShrink: 0,
          }}
        >
          +
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={e => { if (e.target.files?.length) onFiles(e.target.files); e.target.value = ''; }}
            style={{ display: 'none' }}
          />
        </label>
        <InfoTooltip text={t('approval.attachImageHint')} />
      </div>
    </>
  );
}

const wrapStyle: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#f8fafc', padding: 16,
};
// Fixed viewport-relative height (not `height: auto`) is the actual fix for
// the reported bug — the old card grew without bound as the thread filled
// up, pushing the header off screen. `overflow: hidden` here means ONLY the
// two inner scroll regions below (thread list, and — if ever needed — the
// preview) can grow; the card itself never does.
const cardStyle: React.CSSProperties = {
  background: 'white', borderRadius: 12, width: '100%', maxWidth: 920,
  height: 'min(88vh, 780px)',
  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
// `approval-body` class (see the <style> block in ApprovalPage below) is what
// actually switches this row to a column below 720px — inline style objects
// can't express a media query, so that one breakpoint rule lives in CSS while
// everything else here stays inline for consistency with the rest of the file.
const bodyStyle: React.CSSProperties = {
  display: 'flex', flex: 1, minHeight: 0,
};
// `overflow: 'hidden'` (not `auto`) — the preview is meant to always FIT this
// column exactly (ApprovalPreview fills `height: 100%`; both PreviewView's
// own `object-fit: contain` and the R3F <Canvas>'s ResizeObserver adapt to
// whatever box they're given), never to scroll internally.
// `isolation: 'isolate'` is the load-bearing property here, not `overflow`:
// the react-three-fiber <Canvas> is WebGL-rendered and gets GPU-composited
// on its own layer — plain `overflow: hidden`/`auto` on an ancestor clips the
// canvas ELEMENT's box but Chromium does not reliably clip that element's
// actual painted (composited) pixels against it, so on mobile (where this
// column also gets a fixed `height` cap, see the <style> block below) the
// canvas kept bleeding past the column boundary into the thread list
// underneath — confirmed via a getBoundingClientRect() dump showing the
// column's own box was correctly sized/clipped, only the canvas's paint
// wasn't respecting it. `isolation: isolate` forces this column into its own
// stacking context, which makes the browser actually clip composited
// descendants (including WebGL canvases) to it — `overflow: hidden` alone
// does not. `boxSizing: 'border-box'` also matters: without it, this
// element's own `padding` would add on top of the child's `height: 100%`,
// again exceeding the column's real assigned height.
const leftColStyle: React.CSSProperties = {
  flex: '1 1 45%', padding: 20, overflow: 'hidden', borderRight: '1px solid #e2e8f0',
  boxSizing: 'border-box', isolation: 'isolate',
};
const rightColStyle: React.CSSProperties = {
  flex: '1 1 55%', display: 'flex', flexDirection: 'column', minHeight: 0,
};
const threadScrollStyle: React.CSSProperties = {
  flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 20px',
};
// `maxHeight` + `overflowY: 'auto'` (not just `flexShrink: 0`) — with the
// "request changes" note box + image picker open, this composer's own content
// can be taller than the space left after the thread's `flex: 1`, and this
// card never grows past its own fixed height (see `cardStyle`). Without a
// scroll here, that pushed the Approve/Reject buttons below the visible card
// entirely on shorter viewports — the reported "Approve button is missing"
// bug, not a rendering condition, just an overflowed composer with nowhere
// to scroll.
const composerStyle: React.CSSProperties = {
  flexShrink: 0, padding: '12px 20px 20px', borderTop: '1px solid #e2e8f0',
  maxHeight: '60%', overflowY: 'auto',
};
